/* eslint-disable no-underscore-dangle */
import { Chip, Icon, ListItemIcon, Menu, MenuItem } from "@mui/material";
import { PUBLIC, PRIVATE, AccessLabel } from "contants";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { updateAsset } from "redux-react/reducers/assetReducer";
import SourceActions from "redux-react/actions/sourceActions";
import { t } from "i18next";
import { translate } from "helpers/i18n";
import { display, hide } from "redux-react/reducers/snackBarReducer";
import MDButton from "components/Basics/MDButton";
import { useNavigate } from "react-router-dom";

/**
 * Asset access chip component, with menu to change access
 * @param {string} access - current access value
 * @param {string} format - asset format
 * @param {string} _id - asset id
 * @param {boolean} allowChange - if true, user can change access, else it's read only
 * @returns {JSX.Element} AccessChip component
 */
const AccessChip = ({
	isSelected = false,
	access = "private",
	format,
	_id,
	allowChange = true
}) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const open = Boolean(anchorEl);

	const getAcessColor = value => {
		if (value === "public") return "success";
		if (value === "private") return "warning";
		return "warning";
	};

	const handleClickListItem = e => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
	};

	const handleMenuItemClick = selected => {
		setAnchorEl(null);
		const onSuccessCallback = res => {
			dispatch(updateAsset({ ...res, id: res._id }));
		};
		dispatch(
			SourceActions.updateAsset(_id, { access: selected }, onSuccessCallback, res => {
				const errorMessage = res?.response?.data?.error?.message || res?.response?.data?.error;
				const validation = res?.response?.data?.error?.validation || false;
				const type = res?.response?.data?.error?.type || "error";

				const translation = translate(errorMessage);
				dispatch(
					display({
						type,
						message: t(translation),
						validation: validation ? (
							<>
								<MDButton
									variant="contained"
									color="info"
									sx={{ ml: 1 }}
									onClick={() => {
										dispatch(hide());
										navigate(`/settings`);
									}}
								>
									{t("SETTINGS.settings")}
								</MDButton>
								<MDButton
									sx={{ ml: 1 }}
									onClick={() => {
										dispatch(hide());
									}}
								>
									{t("SETTINGS.close")}
								</MDButton>
							</>
						) : null
					})
				);
			})
		);
	};

	const handleClose = e => {
		e.stopPropagation();
		e.preventDefault();
		setAnchorEl(null);
	};

	const label = access ? t(`ASSETS.ACCESS.${access}`) : PRIVATE;

	return (
		<>
			<Chip
				disabled={["WebPage", "sitemap", "theme"].includes(format)}
				label={label}
				size="small"
				color={getAcessColor(access)}
				variant={isSelected ? "filled" : "outlined"}
				onClick={allowChange ? handleClickListItem : null}
				icon={access === PUBLIC ? <Icon>lock_open</Icon> : <Icon>lock_outline</Icon>}
			/>
			<Menu
				id="lock-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				slotProps={{
					"aria-labelledby": "lock-button",
					role: "listbox"
				}}
			>
				{AccessLabel.map(option => (
					<MenuItem
						key={option.value}
						disabled={option.value === access}
						selected={option.value === access}
						onClick={e => {
							e.stopPropagation();
							handleMenuItemClick(option.value);
						}}
					>
						<ListItemIcon>
							{option.value === PUBLIC ? (
								<Icon fontSize="small">lock_open</Icon>
							) : (
								<Icon fontSize="small">lock_outline</Icon>
							)}
						</ListItemIcon>
						{t(option.label)}
					</MenuItem>
				))}
			</Menu>
		</>
	);
};

export default AccessChip;
